import '@fontsource/urbanist/100.css'
import '@fontsource/urbanist/200.css'
import '@fontsource/urbanist/300.css'
import '@fontsource/urbanist/400.css'
import '@fontsource/urbanist/500.css'
import '@fontsource/urbanist/600.css'
import '@fontsource/urbanist/700.css'
import '@fontsource/urbanist/800.css'
import '@fontsource/urbanist/900.css'
import { createTheme } from '@mui/material/styles'
import type {} from '@mui/material/themeCssVarsAugmentation'

declare module '@mui/material/styles' {
    interface Palette {
        custom: {
            lightgreen: string
            lightviolet: string
            black: string
            gray: string
            green: string
            pink: string
            yellow: string
        }
    }
    interface PaletteOptions {
        custom?: {
            lightgreen?: string
            lightviolet?: string
            black?: string
            gray?: string
            green?: string
            pink?: string
            yellow?: string
        }
    }
}

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#262626',
            light: '#404040',
            dark: '#171717',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#dc004e',
            light: '#ff5983',
            dark: '#9a0036',
            contrastText: '#fff',
        },
        text: {
            primary: '#2D2D2D',
            secondary: '#797979',
        },
        custom: {
            lightgreen: '#D2EFE4',
            lightviolet: '#D4DDF9',
            black: '#2D2D2D',
            gray: '#E5DEDB',
            green: '#5AD4A7',
            pink: '#F9489B',
            yellow: '#FFCE46',
        },
    },
    typography: {
        fontFamily: ['Urbanist', 'sans-serif'].join(','),
        fontSize: 16,
        body1: {
            fontSize: '16px',
            fontWeight: 400,
        },
        h1: {
            fontSize: '32px',
            fontWeight: 600,
        },
        h2: {
            fontSize: '24px',
            fontWeight: 400,
        },
        h6: {
            fontSize: '24px',
            fontWeight: 600,
        },
        button: {
            fontFamily: 'Urbanist, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            textTransform: 'none',
            lineHeight: 1,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: '10px 24px',
                    backgroundColor: '#2d2d2d',
                    color: '#ffffff',
                    gap: '6px',
                    boxSizing: 'border-box',
                    '&:hover': { backgroundColor: '#444' },
                    '&:active': { backgroundColor: '#1a1a1a' },
                },
                outlined: {
                    backgroundColor: '#fff',
                    border: '1px solid #bbb',
                    color: '#2d2d2d',
                    '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bbb' },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { fontSize: '14px', fontWeight: 400, color: '#404045' },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&:not(.MuiInputBase-multiline)': { height: 42 },
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C3CFDB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C3CFDB' },
                    '&.Mui-focused': {
                        boxShadow: '0 0 0 4px rgba(73,110,226,0.5)',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#496EE2', borderWidth: '1px' },
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#F0F0F0',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C3CFDB' },
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#C3CFDB' },
                },
                input: {
                    padding: '0 12px',
                    fontSize: '16px',
                    color: '#404045',
                    '&:not(textarea)': { height: '100%', boxSizing: 'border-box' as const },
                    '&::placeholder': { color: '#C4C4C4', opacity: 1 },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: { padding: '0 12px', minHeight: 'unset', display: 'flex', alignItems: 'center' },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    color: '#6B6B6B',
                    marginLeft: 0,
                    '&.Mui-error': { color: '#F02B6A' },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: { borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: { padding: '40px', '&:last-child': { paddingBottom: '40px' } },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: { fontSize: '14px' },
            },
        },
    },
})

export default theme
